import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

interface HeaderProps {
  onCategorySelect: (url: string) => void;
}

export default function Header({ onCategorySelect }: HeaderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileAnimating, setMobileAnimating] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
      setMobileAnimating(true);
    } else {
      document.body.style.overflow = '';
      setMobileAnimating(true);
      const timer = setTimeout(() => setMobileAnimating(false), 400);
      return () => clearTimeout(timer);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const handleCategoryClick = useCallback((url: string) => {
    setIsOpen(false);
    setMobileOpen(false);
    onCategorySelect(url);
  }, [onCategorySelect]);

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
  }, []);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background-50/80 backdrop-blur-md border-b border-background-200/60">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              className="flex items-center gap-2 no-underline"
              onClick={closeMobile}
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-search-eye-line text-xl text-primary-500"></i>
              </div>
              <span className="font-heading text-lg font-semibold text-foreground-900 whitespace-nowrap">
                Kakobuy Finds
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              <Link
                to="/kakobuy-spreadsheet-guide"
                className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
              >
                Full Guide
              </Link>
              <Link
                to="/kakobuy-beginners-guide"
                className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
              >
                Beginner&apos;s Guide
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
              >
                Kakobuy Sheet
              </a>
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 border border-background-200/70 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <span>Browse Categories</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`ri-arrow-down-s-line text-sm transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}></i>
                  </div>
                </button>

                {isOpen && (
                  <div className="absolute top-full mt-2 right-0 w-72 bg-background-50 border border-background-200/70 rounded-xl overflow-hidden shadow-lg max-h-96 overflow-y-auto">
                    <div className="p-2">
                      {categories.map((cat) => (
                        <button
                          key={cat.id}
                          onClick={() => handleCategoryClick(cat.url)}
                          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-left text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-all duration-150 cursor-pointer whitespace-nowrap"
                        >
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                            <i className={`${cat.icon} text-sm`}></i>
                          </div>
                          <span>{cat.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
              >
                Buy It Now
              </a>
            </nav>

            <div className="flex md:hidden items-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
              >
                Buy Now
              </a>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 border border-background-200/70 text-foreground-700 hover:border-primary-300 transition-all duration-200 cursor-pointer"
                aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              >
                <i className={`text-lg transition-transform duration-300 ${mobileOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      {(mobileOpen || mobileAnimating) && (
        <div
          className={`fixed inset-0 z-40 md:hidden transition-all duration-400 ${
            mobileOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
        >
          <div
            className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm"
            onClick={closeMobile}
          ></div>

          <div
            className={`absolute top-0 right-0 w-full max-w-sm h-full bg-background-50 shadow-2xl flex flex-col transition-transform duration-400 ease-out ${
              mobileOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className="flex items-center justify-between px-4 h-16 border-b border-background-200/60 flex-shrink-0">
              <Link to="/" className="flex items-center gap-2 no-underline" onClick={closeMobile}>
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-search-eye-line text-xl text-primary-500"></i>
                </div>
                <span className="font-heading text-lg font-semibold text-foreground-900 whitespace-nowrap">
                  Kakobuy Finds
                </span>
              </Link>
              <button
                onClick={closeMobile}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 border border-background-200/70 text-foreground-700 hover:border-primary-300 transition-all duration-200 cursor-pointer"
                aria-label="Close menu"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-4">
              <div className="px-4 mb-6">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobile}
                  className="block w-full text-center px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap mb-3"
                >
                  Kakobuy Sheet &mdash; Buy It Now
                </a>
              </div>

              <div className="px-4 mb-6">
                <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-1">
                  Guides &amp; Resources
                </p>
                <div className="space-y-1">
                  <Link
                    to="/kakobuy-spreadsheet-guide"
                    onClick={closeMobile}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-all duration-150 whitespace-nowrap"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-article-line text-sm"></i>
                    </div>
                    <div className="text-left">
                      <div className="font-medium">Full Guide</div>
                      <div className="text-xs text-foreground-400">Complete spreadsheet walkthrough</div>
                    </div>
                  </Link>
                  <Link
                    to="/kakobuy-beginners-guide"
                    onClick={closeMobile}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-all duration-150 whitespace-nowrap"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-book-open-line text-sm"></i>
                    </div>
                    <div className="text-left">
                      <div className="font-medium">Beginner&apos;s Guide</div>
                      <div className="text-xs text-foreground-400">First-time shopper tutorial</div>
                    </div>
                  </Link>
                  <Link
                    to="/kakobuy-safety-guide"
                    onClick={closeMobile}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-all duration-150 whitespace-nowrap"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-shield-check-line text-sm"></i>
                    </div>
                    <div className="text-left">
                      <div className="font-medium">Safety Guide</div>
                      <div className="text-xs text-foreground-400">Secure shopping practices</div>
                    </div>
                  </Link>
                </div>
              </div>

              <div className="px-4 mb-6">
                <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-1">
                  Platform Comparisons
                </p>
                <div className="space-y-1">
                  <Link
                    to="/kakobuy-vs-cssbuy"
                    onClick={closeMobile}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-all duration-150 whitespace-nowrap"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-scales-3-line text-sm"></i>
                    </div>
                    <span>Kakobuy vs CSSBuy</span>
                  </Link>
                  <Link
                    to="/kakobuy-vs-superbuy"
                    onClick={closeMobile}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-all duration-150 whitespace-nowrap"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-scales-3-line text-sm"></i>
                    </div>
                    <span>Kakobuy vs Superbuy</span>
                  </Link>
                  <Link
                    to="/kakobuy-vs-oopbuy"
                    onClick={closeMobile}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-all duration-150 whitespace-nowrap"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-scales-3-line text-sm"></i>
                    </div>
                    <span>Kakobuy vs Oopbuy</span>
                  </Link>
                </div>
              </div>

              <div className="px-4">
                <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-1">
                  Product Categories
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryClick(cat.url)}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs text-foreground-700 bg-background-100 border border-background-200/70 hover:bg-primary-50 hover:border-primary-300 hover:text-primary-600 transition-all duration-150 cursor-pointer whitespace-nowrap text-left"
                    >
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 flex-shrink-0">
                        <i className={`${cat.icon} text-xs`}></i>
                      </div>
                      <span className="leading-tight">{cat.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}